// src/services/likeApi.js — the browser's likes and shares, through the server.
// The twin (by hand — it needs the browser's auth and backend URL) of the
// phone's services/likeApi.ts. A like is a document of its own now
// (server/functions/likeRoutes.js); past 500 likers the post cannot say whether
// *you* liked it, so this holds the tab's one "did I like this?" store
// (services/likeLedger — generated from the phone's TypeScript).
import { useEffect, useRef, useState } from 'react';
import { auth } from '../config/firebase';
import { BACKEND_URL } from './socket';
import { createMyLikes } from './likeLedger';
import { myReaction } from './reactions';

async function call(path, body) {
  let token = null;
  try { token = await auth.currentUser?.getIdToken(); } catch { token = null; }
  if (!token) return { ok: false };
  let res;
  try {
    res = await fetch(`${BACKEND_URL}${path}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      body: JSON.stringify(body),
    });
  } catch { return { ok: false }; }
  let j = null;
  try { j = await res.json(); } catch { j = null; }
  if (!res.ok || !j || j.ok === false) return { ok: false, status: res.status || 500, error: (j && j.error) || '' };
  return { ...j, ok: true };
}

/* 😍 MY KIND OF LIKE — for posts whose map no longer names me, and for the
   moment between a press and the server's answer (services/reactions) */
const knownReactions = new Map();
const rxListeners = new Set();
export function reactionOf(post, uid, liked) {
  if (!liked) return null;
  const id = post && post.id ? String(post.id) : '';
  return (id && knownReactions.get(id)) || myReaction(post, uid, liked);
}
export function rememberReaction(postId, r) {
  if (r) knownReactions.set(String(postId), r); else knownReactions.delete(String(postId));
  rxListeners.forEach((fn) => { try { fn(); } catch {} });
}
export function onReactionChange(fn) { rxListeners.add(fn); return () => rxListeners.delete(fn); }

/** The tab's one answer to "did I like this?". */
export const myLikes = createMyLikes(async (postIds) => {
  const r = await call('/api/vibes/likes/mine', { postIds });
  if (r.ok && r.reactions && typeof r.reactions === 'object') for (const [id, k] of Object.entries(r.reactions)) knownReactions.set(String(id), k);
  return r.ok ? { ok: true, liked: Array.isArray(r.liked) ? r.liked.map(String) : [] } : { ok: false };
});

/* 🟥 one post's presses go in order — two parallel requests can land the other way round */
const chains = new Map();
async function sendLikeNow(postId, liked, tries, reaction = 'like') {
  let last = { ok: false };
  for (let i = 0; i < tries; i += 1) {
    // eslint-disable-next-line no-await-in-loop
    last = await call('/api/vibes/like', { postId, liked, reaction });
    if (last.ok || last.status) return last;
    // eslint-disable-next-line no-await-in-loop
    await new Promise((r) => setTimeout(r, 600 * (i + 1)));
  }
  return last;
}
/** Make my like on a post be `liked`. Retries a server that was not reached. */
export function sendLike(postId, liked, tries = 3, reaction = 'like') {
  const prev = chains.get(postId) || Promise.resolve();
  const run = prev.then(() => sendLikeNow(postId, liked, tries, reaction));
  const tail = run.catch(() => null);
  chains.set(postId, tail);
  tail.then(() => { if (chains.get(postId) === tail) chains.delete(postId); });
  return run;
}

/** One page of "liked by": { people: [{uid,name,image}], next, total }. */
export const fetchLikers = (postId, cursor, kind) => call('/api/vibes/likes/list', { postId, cursor: cursor || null, ...(kind ? { kind } : {}) });

/** A share's counter (and, while there is room, the sharer's name). */
export const countShare = (postId) => call('/api/vibes/share/count', { postId });

/** Is the heart full — re-drawn when this post's answer changes, and only then. */
export function useLikedByMe(post, uid) {
  const [, bump] = useState(0);
  const id = post && post.id ? String(post.id) : '';
  const liked = myLikes.likedBy(post, uid);
  const live = useRef({ post, liked });
  live.current = { post, liked };
  useEffect(() => {
    if (uid) myLikes.setMe(uid);
    if (!id) return undefined;
    myLikes.want(post);
    return myLikes.subscribe(() => {
      if (myLikes.likedBy(live.current.post, uid) !== live.current.liked) bump((n) => (n + 1) % 1e9);
    });
  }, [id, uid]); // eslint-disable-line react-hooks/exhaustive-deps
  return liked;
}
